import { useDroppable } from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy } from '@dnd-kit/sortable';
import { LanguageCode } from '@sparted/shared-library/business/types';
import { t } from 'i18next';
import React, { type FC, useCallback, useMemo } from 'react';

import SDailySessionContainer from 'Components/structural/SDailySessionContainer/SDailySessionContainer';
import { useTypedSelector } from 'Libs/redux/utils';
import { DraggableContentCard } from 'Pages/Activity/Campaign/components/DraggableContentCard/DraggableContentCard';
import {
  DROP_AREA,
  SESSION_ID_OFFSET,
} from 'Pages/Activity/Campaign/components/DraggableContentCard/hooks/useContentsDragAndDrop';
import { formatTranslationStatuses } from 'Pages/Content/ContentList/utils';
import type { PartialContentForCampaignForm } from 'Store/entities/activity/activity.type';

import styles from './CampaignSession.style';
import { redirect } from 'Libs/routing';

export type CampaignSessionProps = {
  id: number;
  session: PartialContentForCampaignForm[];
  sessionNumber: number;
  locked: boolean;
  startDate: string;
  selectedLanguage: LanguageCode;
  random: boolean;
  onAddContent: () => void;
  onDeleteContent: (id: string) => void;
  onDeleteSession: () => void;
};

export const CampaignSession: FC<CampaignSessionProps> = ({
  id,
  session,
  sessionNumber,
  locked,
  startDate,
  selectedLanguage,
  random,
  onAddContent,
  onDeleteContent,
  onDeleteSession,
}) => {
  const sessionId = id + SESSION_ID_OFFSET;
  const { setNodeRef: setOuterDroppableRef, over } = useDroppable({ id: sessionId, data: { type: DROP_AREA } });
  const sessionSet = useMemo(() => new Set(session.map(({ id }) => id)), [session]);
  const draggingOver = over?.id === sessionId || sessionSet.has(over?.id?.toString() || '');
  const { campaign } = useTypedSelector((state) => state.entities.activity.edition);

  if (!campaign) return null;

  const isDefaultLanguage = useMemo(() => selectedLanguage === campaign.languages[0], [selectedLanguage, campaign]);

  const ListWrapper = useCallback(
    ({ children, ...props }) => (
      <div ref={setOuterDroppableRef} {...props}>
        {children}
      </div>
    ),
    [setOuterDroppableRef],
  );

  const handleContentRedirect = useCallback((e: React.MouseEvent<HTMLDivElement> | undefined, contentId: number) => {
    if (e?.metaKey) {
      e.preventDefault();
      e.stopPropagation();
      window.open(`/content/${contentId}`, '_blank');
      return;
    }
    redirect(`/content/${contentId}`);
  }, []);

  return (
    <SDailySessionContainer
      sessionNumber={sessionNumber}
      locked={locked}
      onAdd={onAddContent}
      onDelete={onDeleteSession}
      contentCount={session.length}
      startDate={startDate}
      style={styles.dailySessionContainer}
      draggingOver={draggingOver}
      Wrapper={ListWrapper}
      disabledAdd={!isDefaultLanguage}
      disabledDelete={!isDefaultLanguage || random}
    >
      <SortableContext items={session} strategy={rectSortingStrategy} disabled={random}>
        {session.map((content) => {
          return (
            <DraggableContentCard
              draggable={!locked}
              dragId={content.id}
              key={`item-${content.id}`}
              content={content}
              onClick={(e) => handleContentRedirect(e, content.contentId)}
              contentCardProps={{
                id: `#${content.identifier || content.id}`,
                canShowOverlay: true,
                selectable: false,
                previewable: false,
                title: content.name?.[selectedLanguage] || '',
                type: content.gameType,
                translationStatuses: formatTranslationStatuses(content.statuses, content.contentId),
                imageUrl: content?.coverImage?.url || '',
                canBeDragged: true,
                locked: locked,
                displayOption: true,
                archived: content.archived,
                style: {
                  ...(!isDefaultLanguage && content.statuses?.[selectedLanguage] !== 'validated'
                    ? styles.dailySessionContentCardLocked
                    : {}),
                },
                options: [
                  {
                    id: 1,
                    text: t('activities:scheduling.edit_content'),
                    onClick: (_, e?: React.MouseEvent<HTMLDivElement>) => handleContentRedirect(e, content.contentId),
                  },
                  {
                    id: 2,
                    text: t('activities:scheduling.remove_content'),
                    onClick: () => onDeleteContent(content.id),
                  },
                ],
              }}
            />
          );
        })}
      </SortableContext>
    </SDailySessionContainer>
  );
};
